/* DC pit board countdown (markup: snippets/dc-pitboard.liquid, styles: dc-pitboard.css).
   One shared one-second ticker drives every board on the page. The show instant is fixed
   at 2026-11-21T09:00:00+10:00 (Brisbane, no DST) and read from data-target; it is NOT
   the store timezone, which is wrong on this store.

   Schedule (all against that instant, T):
     before T, 2 days or more   DOORS / <calendar days left in Brisbane> / DAYS
     before T, under 2 days     DOORS / <whole hours left> / HOURS
     before T, under 2 hours    DOORS / <minutes left, rounded up> / MINS
       and every 20 s the board cycles through data-cycle (EARLY 9 AM, OPEN 21 NOV) and back.
     T to T+1h                  DOORS / OPEN / 9AM      (data-day[0])
     T+1h to T+8h               ON / NOW / 10-5         (data-day[1])
     after T+8h                 THANK / YOU / EAGLEBY   (data-day[2]), no cycling on the day.
   Never a seconds row: a pit board is not a stopwatch.

   Plate changes: the old plate lifts out of the channel, the new one drops in (CSS
   animations, rows staggered 70 ms top to bottom). Reduced motion: text swaps in place.
   Screen readers: role="timer" aria-live="off" on the board, plus a visually hidden
   sentence ("Doors open in 71 days.") refreshed at most once a minute.

   Loaded once per page even when two snippets render (window.__dcPitboard guard); init
   is idempotent (el.__dcPb) and runs on DOMContentLoaded and shopify:section:load. */
(() => {
  if (window.__dcPitboard) return;
  const HOUR = 36e5;
  const DAY = 864e5;
  const BRISBANE = 10 * HOUR;
  const CYCLE_MS = 20000;
  const STAGGER_MS = 70;
  const FALLBACK_TARGET = '2026-11-21T09:00:00+10:00';
  const FALLBACK_CYCLE = [['EARLY', '9', 'AM'], ['OPEN', '21', 'NOV']];
  const FALLBACK_DAY = [['DOORS', 'OPEN', '9AM'], ['ON', 'NOW', '10-5'], ['THANK', 'YOU', 'EAGLEBY']];

  const boards = new Set();
  let timer = 0;
  let epoch = 0;
  const now = () => (typeof window.__dcPitboardNow === 'function' ? window.__dcPitboardNow() : Date.now());
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const readJSON = (el, attr, fallback) => {
    try { const v = JSON.parse(el.getAttribute(attr) || ''); return Array.isArray(v) && v.length ? v : fallback; } catch (e) { return fallback; }
  };

  /* Pure schedule: what the board should read at instant t for target T. */
  const compute = (T, t, cycle, day) => {
    const left = T - t;
    if (left > 0) {
      let first;
      let sr;
      if (left >= 2 * DAY) {
        const days = Math.floor((T + BRISBANE) / DAY) - Math.floor((t + BRISBANE) / DAY);
        first = ['DOORS', String(days), days === 1 ? 'DAY' : 'DAYS'];
        sr = 'Doors open in ' + days + (days === 1 ? ' day.' : ' days.');
      } else if (left >= 2 * HOUR) {
        const h = Math.floor(left / HOUR);
        first = ['DOORS', String(h), 'HOURS'];
        sr = 'Doors open in ' + h + ' hours.';
      } else {
        const m = Math.max(1, Math.ceil(left / 60000));
        first = ['DOORS', String(m), m === 1 ? 'MIN' : 'MINS'];
        sr = 'Doors open in ' + m + (m === 1 ? ' minute.' : ' minutes.');
      }
      return { phase: 'pre', frames: [first].concat(cycle), sr: sr };
    }
    const since = -left;
    if (since < HOUR) return { phase: 'day', frames: [day[0]], sr: 'Doors are open. Early Access from 9am, general entry from 10am.' };
    if (since < 8 * HOUR) return { phase: 'day', frames: [day[1]], sr: 'The show is on now, 10am to 5pm.' };
    return { phase: 'day', frames: [day[2] || day[day.length - 1]], sr: 'Thank you Eagleby. The show has closed for today.' };
  };

  const fit = (plate, text) => {
    plate.textContent = text;
    plate.classList.toggle('is-long', text.length > 5);
  };

  /* Swap one row. Reduced motion (or a first sync before anything was seen): in place. */
  const swap = (slot, text, delay, animate) => {
    const current = slot.querySelector('.dc-pitboard__plate:not(.is-out)');
    if (!animate || !current) {
      if (current) { fit(current, text); return; }
      const p = document.createElement('span');
      p.className = 'dc-pitboard__plate';
      fit(p, text);
      slot.appendChild(p);
      return;
    }
    current.classList.add('is-out');
    current.style.animationDelay = delay + 'ms';
    const drop = () => { if (current.parentNode) current.remove(); };
    current.addEventListener('animationend', drop, { once: true });
    setTimeout(drop, delay + 700);
    const next = document.createElement('span');
    next.className = 'dc-pitboard__plate is-in';
    fit(next, text);
    next.style.animationDelay = (delay + 110) + 'ms';
    next.addEventListener('animationend', () => { next.classList.remove('is-in'); next.style.animationDelay = ''; }, { once: true });
    slot.appendChild(next);
  };

  const render = (b, t) => {
    const state = compute(b.target, t, b.cycle, b.day);
    const idx = state.frames.length > 1 ? Math.floor((t - epoch) / CYCLE_MS) % state.frames.length : 0;
    const frame = state.frames[idx] || state.frames[0];
    const animate = b.seen && !reduced();
    let step = 0;
    for (let i = 0; i < 3; i++) {
      const word = String(frame[i] == null ? '' : frame[i]);
      if (b.words[i] === word) continue;
      b.words[i] = word;
      if (b.slots[i]) swap(b.slots[i], word, step * STAGGER_MS, animate);
      step++;
    }
    b.seen = true;
    b.el.dataset.phase = state.phase;
    b.el.dataset.frame = String(idx);
    if (b.sr && state.sr !== b.srText && (!b.srAt || t - b.srAt >= 60000 || state.phase !== b.srPhase)) {
      b.sr.textContent = state.sr;
      b.srText = state.sr;
      b.srAt = t;
      b.srPhase = state.phase;
    }
  };

  const tick = () => {
    const t = now();
    boards.forEach((b) => { if (!b.el.isConnected) { boards.delete(b); return; } render(b, t); });
    strips.forEach((st) => { if (!st.el.isConnected) { strips.delete(st); return; } renderStrip(st, t); });
    if (!boards.size && !strips.size && timer) { clearInterval(timer); timer = 0; }
  };

  const init = (el) => {
    if (!el || el.__dcPb) return;
    const slots = Array.from(el.querySelectorAll('[data-dc-pb-row]')).sort((a, c) => +a.dataset.dcPbRow - +c.dataset.dcPbRow);
    if (slots.length < 3) return;
    const target = Date.parse(el.dataset.target || FALLBACK_TARGET);
    const b = {
      el: el,
      slots: slots,
      target: isNaN(target) ? Date.parse(FALLBACK_TARGET) : target,
      cycle: readJSON(el, 'data-cycle', FALLBACK_CYCLE),
      day: readJSON(el, 'data-day', FALLBACK_DAY),
      sr: el.querySelector('[data-dc-pb-sr]'),
      words: slots.map((s) => { const p = s.querySelector('.dc-pitboard__plate'); return p ? p.textContent.trim() : ''; }),
      seen: false,
      srText: '',
      srAt: 0,
      srPhase: '',
    };
    el.__dcPb = b;
    boards.add(b);
    if (!epoch) epoch = now();
    render(b, now());
    if (!timer) timer = setInterval(tick, 1000);
  };

  /* Countdown strip: same clock, same schedule phases, DAYS : HRS : MIN : SEC every second. */
  const strips = new Set();
  const pad2 = (n) => (n < 10 ? '0' : '') + n;
  const renderStrip = (st, t) => {
    const left = st.target - t;
    const lead = st.el.querySelector('[data-dc-strip-lead]');
    let phase = 'pre';
    let sr;
    if (left > 0) {
      const d = Math.floor(left / DAY);
      const h = Math.floor((left % DAY) / HOUR);
      const m = Math.floor((left % HOUR) / 60000);
      const s = Math.floor((left % 60000) / 1000);
      const vals = { d: String(d), h: pad2(h), m: pad2(m), s: pad2(s) };
      Object.keys(vals).forEach((k) => { const n = st.n[k]; if (n && n.textContent !== vals[k]) n.textContent = vals[k]; });
      if (lead && lead.textContent !== st.lead) lead.textContent = st.lead;
      sr = st.lead + ' ' + d + ' days, ' + h + ' hours and ' + m + ' minutes.';
    } else {
      phase = 'day';
      const since = -left;
      const text = since < HOUR ? 'Doors open. Early Access from 9am, general entry from 10am.' : since < 8 * HOUR ? 'On now. 10am to 5pm.' : 'Thank you Eagleby. See you next year.';
      if (lead && lead.textContent !== text) lead.textContent = text;
      sr = text;
    }
    if (st.el.dataset.phase !== phase) { st.el.dataset.phase = phase; if (st.cells) st.cells.hidden = phase !== 'pre'; }
    if (st.sr && sr !== st.srText && (!st.srAt || t - st.srAt >= 60000 || phase !== st.srPhase)) { st.sr.textContent = sr; st.srText = sr; st.srAt = t; st.srPhase = phase; }
  };
  const initStrip = (el) => {
    if (!el || el.__dcStrip) return;
    const target = Date.parse(el.dataset.target || FALLBACK_TARGET);
    const n = {};
    el.querySelectorAll('[data-dc-strip-n]').forEach((b) => { n[b.dataset.dcStripN] = b; });
    const leadEl = el.querySelector('[data-dc-strip-lead]');
    const st = { el: el, target: isNaN(target) ? Date.parse(FALLBACK_TARGET) : target, n: n, cells: el.querySelector('[data-dc-strip-cells]'), lead: leadEl ? leadEl.textContent.trim() : 'Doors open in', sr: el.querySelector('[data-dc-strip-sr]'), srText: '', srAt: 0, srPhase: '' };
    if (el.dataset.phase === 'day' && leadEl) st.lead = 'Doors open in';
    el.__dcStrip = st;
    strips.add(st);
    renderStrip(st, now());
    if (!timer) timer = setInterval(tick, 1000);
  };

  const initAll = (root) => { (root || document).querySelectorAll('[data-dc-pitboard]').forEach(init); (root || document).querySelectorAll('[data-dc-strip]').forEach(initStrip); };
  const teardown = (el) => { if (el && el.__dcPb) { boards.delete(el.__dcPb); el.__dcPb = null; } };

  window.__dcPitboard = { boards: boards, init: init, initAll: initAll, tick: tick, compute: compute, resetEpoch: () => { epoch = now(); } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('[data-dc-pitboard]').forEach(teardown));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) tick(); });
})();
